import { Button, Chip, DataTable, DialogHandler, Dropdown, EmptyContent, EStorageType, FullBar, GenericWrapperContext, Header, Icon, LoadingContainer, Search, stableSort, SubNav, TDataTableRowData, TOrder, TTemplateShareAccounPayload, TTemplateSharedAccount, TTemplateSharePayload } from "@cakemail-org/ui-components-v2"
import { MouseEvent, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import "./styles.scss"
import { SelectChangeEvent, Stack } from "@mui/material"
import { ETemplateSharingTabs, TTemplateSharingFilters } from "./types"
import { getCurrentStoredFilters, setCurrentStoredFilters } from "../../../utils/components"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"
import { TemplateDetailContainerContext } from "../../../container/templates/context"
import { NameTableCol } from "../../tables/nameTableCol"


export default function Sharing() {
    const filterStorageKey = "templateAccountSharingFilters"
    const genericWrapper = useContext(GenericWrapperContext)
    const templateContext = useContext(TemplateDetailContainerContext)
    const { t, i18n } = useTranslation()

    const initFilters: TTemplateSharingFilters = getCurrentStoredFilters({ key: filterStorageKey, accountId: String(genericWrapper.account?.id) })

    const [order, setOrder] = useState<TOrder>(initFilters?.order || "asc")
    const [orderBy, setOrderBy] = useState<string>(initFilters?.orderBy || "company_name")
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(50)
    const [search, setSearch] = useState<string>(initFilters?.search || "")
    const [tabFilter, setTabFilter] = useState<ETemplateSharingTabs>(initFilters?.tab as ETemplateSharingTabs || ETemplateSharingTabs.all)
    const [openedSearch, setOpenedSearch] = useState(!!initFilters?.search)
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(handleSearchFocus, [openedSearch])

    function fetchAccounts() {
        let accounts: TTemplateSharedAccount[] = templateContext.template.sharing.accounts.filter((a) => {
            return a.company_name.includes(search)
        }
        ) || []

        accounts = stableSort<TTemplateSharedAccount>(accounts, order, orderBy as keyof TTemplateSharedAccount).slice(
            (page - 1) * perPage,
            (page - 1) * perPage + perPage,
        )

        if (tabFilter === ETemplateSharingTabs.shared) {
            accounts = accounts.filter((aVis) => {
                return aVis.visible
            })
        }

        setCurrentStoredFilters<TTemplateSharingFilters>({
            key: filterStorageKey,
            accountId: Number(genericWrapper.account?.id),
            data: {
                search: search,
                order: order,
                orderBy: orderBy,
                tab: tabFilter
            }
        })

        return accounts
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".clientSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
        setPerPage(cPerPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleChangeTabFilter(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setTabFilter(item.name as ETemplateSharingTabs)
    }

    function handleShare(
        { btn, accountPayload, automatic, everyone }:
            { btn?: TButtonRef, accountPayload?: TTemplateShareAccounPayload, automatic?: boolean, everyone?: boolean }) {
        setLoading(true)
        btn && btn.animate()
        let accounts: TTemplateShareAccounPayload[] = []

        if (accountPayload) {
            accounts = templateContext.template?.sharing?.accounts?.map((aVis) => {
                return {
                    visible: aVis.visible,
                    account_id: aVis.account_id
                }
            }).filter((aVis) => {
                if (aVis.account_id == accountPayload.account_id) {
                    return false
                }
                return aVis.visible
            })

            accounts?.push({
                visible: accountPayload.visible,
                account_id: accountPayload.account_id,
            })
        }

        templateContext.handleShare({
            accounts: accounts?.length > 0 ? accounts : undefined,
            automatic,
            everyone
        })
            .then(() => {
                btn && btn.reset()
                setLoading(false)
            })
            .catch(() => {
                btn && btn.reset()
                setLoading(false)
            })
    }

    function handleAutomaticChange(event: SelectChangeEvent<string>) {
        const { value } = event.target
        handleShare({ automatic: (value === "true" ? true : false) })
    }

    function showShareDialog({ unshare, btn }: { unshare?: boolean, btn?: TButtonRef } = {}) {

        const sharedCount = templateContext.template.sharing.accounts.filter((aVis) => {
            return aVis.visible
        })?.length || 0

        if (unshare && sharedCount == 0) {
            DialogHandler.show({
                type: "success",
                title: t("asset-shared-already-none")
            })
            btn && btn.reset()
            return
        } else if (!unshare && sharedCount === templateContext.template.sharing?.accounts?.length) {
            DialogHandler.show({
                type: "success",
                title: t("asset-shared-already-all")
            })
            btn && btn.reset()
            return
        }

        DialogHandler.show({
            title: t(unshare ? "asset-unshareall-dialog-title" : "asset-shareall-dialog-title"),
            content: t("asset-shareall-dialog-desc", { sharedCount, totalCount: templateContext.template.sharing?.accounts?.length || 0 }),
            primaryBtnOnClick: () => {
                DialogHandler.hide()
                if (unshare) {
                    handleShare({ btn, everyone: false })
                } else {
                    handleShare({ btn, everyone: true })
                }
            },
            primaryBtnText: t(unshare ? "general-unshare" : "general-share"),
            secondaryBtnOnClick: () => {
                DialogHandler.hide()
                btn && btn.reset()
            },
            secondaryBtnText: t("general-cancel")
        })
    }

    const columnsData = [
        {
            id: "company_name",
            label: t("clients-table-label-name"),
            sortable: true,
        },
        {
            id: "account_id",
            label: t("clients-table-label-id"),
            sortable: true,
            width: 100
        },
        {
            id: "actions",
            label: <>
                <Button size="small" color="body1" variant="outlined" animated onClick={(e, btn) => {
                    showShareDialog({ btn })
                }}> {t("template-sharing-share-all")}</Button>
                <Button size="small" color="body1" variant="outlined" animated onClick={(e, btn) => { showShareDialog({ unshare: true, btn }) }}>{t("template-sharing-unshare-all")}</Button>
            </>
        }
    ]


    const accounts = fetchAccounts()

    const rows = accounts.map((templateAccount) => {
        const isShared = templateAccount.visible
        return {
            account_id: templateAccount.account_id,
            company_name: {
                id: templateAccount.company_name,
                content: <NameTableCol name={templateAccount.company_name} />
            },

            actions: {
                id: "actions",
                content: <>
                    <Button
                        size="small"
                        startIcon={isShared ? <Icon name="Checkmark10" /> : undefined}
                        color={isShared ? "secondary" : "body1"}
                        variant={isShared ? "contained" : "outlined"}
                        animated
                        onClick={(e, btn) => {
                            handleShare({
                                btn,
                                accountPayload: {
                                    account_id: templateAccount.account_id,
                                    visible: !isShared
                                }
                            })
                        }}
                    >
                        {isShared ?
                            t("general-shared")
                            :
                            t("general-share")
                        }
                    </Button>
                </>
            }
        }
    })

    const sharedCount = templateContext.template.sharing.accounts.filter((aVis) => {
        return aVis.visible
    }).length

    return <Stack className={`accountSharing ${loading ? "loading" : ""}`} justifyContent="flex-start">

        <FullBar
            left={
                <Header
                    key="template-detial-head"
                    postHeading={<Chip color="body1" variant="outlined" label={t("general-not-owned")} />}
                    heading={t("template-sharing-heading")}
                    headingTooltip={t("template-detail-heading-tooltip", { resellerName: genericWrapper.partnerBrand?.name })}
                    support={t("template-detail-support")}
                />
            }
            right={<>
                <Button variant="outlined" color="body1" onClick={handleSearchOpen}><Icon name="Search18" /></Button>
                <Dropdown
                    onChange={handleAutomaticChange}
                    color="body1"
                    value={String(templateContext.template.sharing.automatic)}
                    options={[
                        {
                            text: t("template-sharing-autoshare-on-txt"),
                            desc: t("template-sharing-autoshare-on-desc"),
                            value: "true",
                            disabled: templateContext.template.sharing.automatic
                        },
                        {
                            text: t("template-sharing-autoshare-off-txt"),
                            desc: t("template-sharing-autoshare-off-desc"),
                            value: "false",
                            disabled: !templateContext.template.sharing.automatic
                        }
                    ]}
                />
            </>}
        />

        {openedSearch &&
            <Search useClickAway placeholder={t("template-sharing-search-placeholder")} initValue={search} className="clientSearch" onChange={handleSearchChange} onClose={handleSearchOpen} />
        }

        <SubNav
            className={loading ? "loading" : ""}
            onClick={handleChangeTabFilter}
            initialSelected={tabFilter}
            items={[
                { text: t("template-sharing-tab-all", { count: templateContext.template.sharing.accounts.length }), name: ETemplateSharingTabs.all },
                { text: t("template-sharing-tab-shared", { count: sharedCount }), name: ETemplateSharingTabs.shared }
            ]}
        />

        <LoadingContainer loading={!templateContext.template}>
            {rows.length > 0 &&
                <DataTable
                    initState={{ orderBy: orderBy, rowsPerPage: perPage, order: order }}
                    columnsData={columnsData}
                    rows={rows}
                    totalCount={templateContext.template.sharing.accounts.length ?? undefined}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                />
            }

            {rows?.length === 0 &&
                <EmptyContent
                    title={t("template-sharing-search-empty-title")}
                />
            }
        </LoadingContainer>
    </Stack>
}